import { PlayerScenarioRunnerAPI } from "@manager/playerManager/PlayerManager";
import { inject, injectable } from "inversify";
import { ScenarioElement } from "../../model/playerConfiguration/ScenarioElement";
import { PlaybackData } from "../../model/playerState/PlaybackData";
import { ScenarioElementPlayOptions } from "../../model/scenarioElementPlayOptions/ScenarioElementPlayOptions";
import { ScenarioGlobalState } from "../../model/scenarioGlobalState/ScenarioGlobalState";
import { TYPES } from "../di/types";
import { ResourcesCache } from "../loader/scenarioLoader/ResourcesCache";
import { ScenarioExecutor } from "./ScenarioExecutor";

export interface ScenarioRunner {
  setPlayerAPIDelegate(playerAPI: PlayerScenarioRunnerAPI): void;
  run(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioElement: ScenarioElement,
    scenarioResources: Promise<ResourcesCache>,
    options: ScenarioElementPlayOptions,
  ): void;
  getPlaybackData(): PlaybackData;
  getScenarioGlobalState(): ScenarioGlobalState;
  play(): void;
  pause(): void;
  performAction(ctaId: number, actionId: number, ...args: any[]): void;
  jumpToScenarioElement(scenarioElement: ScenarioElement, progress: number, isAutoplay?: boolean): void;
}

@injectable()
export class ScenarioRunnerImpl implements ScenarioRunner {
  private currentActiveExecutor: ScenarioExecutor;
  private allExecutors: ScenarioExecutor[];

  constructor(
    @inject(TYPES.scenarioRunner.CTAOnlyScenarioExecutor) private ctaOnlyScenarioExecutor: ScenarioExecutor,
    @inject(TYPES.scenarioRunner.VideoDrivenScenarioExecutor) private videoDrivenScenarioExecutor: ScenarioExecutor,
    @inject(TYPES.scenarioRunner.AnimationDrivenScenarioExecutor) private animationDrivenScenarioExecutor: ScenarioExecutor,
  ) {
    this.allExecutors = [ctaOnlyScenarioExecutor, videoDrivenScenarioExecutor, animationDrivenScenarioExecutor];
  }

  setPlayerAPIDelegate(playerAPI: PlayerScenarioRunnerAPI) {
    for (const executor of this.allExecutors) {
      executor.setPlayerAPIDelegate(playerAPI);
    }
  }

  run(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioElement: ScenarioElement,
    scenarioResources: Promise<ResourcesCache>,
    options: ScenarioElementPlayOptions,
  ): Promise<void> {
    scenarioGlobalState = scenarioGlobalState.withNowPlaying(scenarioElement);

    for (const executor of this.allExecutors) {
      executor.cancel();
    }

    if (scenarioElement.video) {
      this.currentActiveExecutor = this.videoDrivenScenarioExecutor;
    } else if (scenarioElement.animations.length) {
      this.currentActiveExecutor = this.animationDrivenScenarioExecutor;
    } else {
      this.currentActiveExecutor = this.ctaOnlyScenarioExecutor;
    }

    return this.currentActiveExecutor.intializeAndRun(scenarioGlobalState, scenarioResources, options);
  }

  getPlaybackData(): PlaybackData {
    return this.currentActiveExecutor && this.currentActiveExecutor.getPlaybackData();
  }

  getScenarioGlobalState(): ScenarioGlobalState {
    return this.currentActiveExecutor && this.currentActiveExecutor.getScenarioGlobalState();
  }

  play(): void {
    this.currentActiveExecutor && this.currentActiveExecutor.play();
  }

  pause(): void {
    this.currentActiveExecutor && this.currentActiveExecutor.pause();
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.currentActiveExecutor && this.currentActiveExecutor.performAction(ctaId, actionId, args);
  }

  jumpToScenarioElement(scenarioElement: ScenarioElement, progress: number, isAutoplay?: boolean): void {
    this.currentActiveExecutor && this.currentActiveExecutor.jumpToScenarioElement(scenarioElement, progress, isAutoplay);
  }
}
